export const heroData = {
  label: "YOKOHAMA HELICOPTER CRUISING",
  title: "Yokohama From the Sky —\nViews You Can't Find on the Ground.",
  subtitle:
    "The skyscrapers of Minato Mirai, the Bay Bridge, the Red Brick Warehouse — a breathtaking panorama that simply cannot be experienced on the ground. First-timers welcome. AIRDOA guides you from booking to boarding.",
  ctaPrimary: { label: "View Pricing & Plans", href: "/pricing" },
  ctaSecondary: { label: "Inquire About Booking", href: "/contact" },
  image:
    "https://readdy.ai/api/search-image?query=Aerial%20panoramic%20view%20of%20Yokohama%20Minato%20Mirai%20skyline%20at%20night%20with%20illuminated%20Landmark%20Tower%2C%20Cosmo%20Clock%20ferris%20wheel%2C%20and%20Bay%20Bridge%2C%20helicopter%20perspective%2C%20deep%20navy%20blue%20sky%20with%20golden%20city%20lights%20reflecting%20on%20calm%20bay%20water%2C%20luxury%20travel%20experience%2C%20cinematic%20atmosphere%2C%20no%20text%2C%20high%20detail%20editorial%20photography&width=1920&height=900&seq=yokhero02&orientation=landscape",
};

export const introData = {
  label: "INTRODUCTION",
  title: "What is Yokohama Helicopter Cruising?",
  paragraphs: [
    "Yokohama Helicopter Cruising is a special experience where you board a helicopter and soar above the city. From a vantage point that can never be seen from the ground, you enjoy a panoramic view of the skyscrapers of Minato Mirai, the 860-meter Bay Bridge, and the historic Red Brick Warehouse — all the iconic landmarks of Yokohama, all at once.",
    "The duration is approximately 15 to 30 minutes. Brief as it is, the overwhelming sense of wonder will stay with you for a lifetime. As the pilot gently gains altitude, the scenery outside the window feels like a scene from a movie. On the sunset course, you witness the sky shift from orange to deep navy. On the night view course, you are treated to a jewel box of light woven by the Bay Bridge and Minato Mirai.",
    "Anniversaries, dates, proposals, sightseeing, gifts — whatever the occasion, this experience delivers an extraordinary moment away from everyday life. AIRDOA is a booking media dedicated to making this experience safe, accessible, and guided every step of the way.",
  ],
  stats: [
    { value: "15–30 min", label: "Duration" },
    { value: "2–3 guests", label: "Capacity" },
    { value: "4 courses", label: "Flight Routes" },
  ],
  image:
    "https://readdy.ai/api/search-image?query=Helicopter%20cabin%20interior%20at%20twilight%20with%20panoramic%20window%20showing%20illuminated%20cityscape%20outside%2C%20soft%20warm%20ambient%20lighting%2C%20leather%20seats%2C%20luxury%20intimate%20atmosphere%2C%20deep%20navy%20sky%20gradient%2C%20editorial%20aviation%20photography%2C%20no%20text&width=800&height=500&seq=yokintro01&orientation=landscape",
};

export const charmData = {
  label: "CHARM",
  title: "Why Yokohama Helicopter Cruising?",
  subtitle: "Adjacent to Tokyo yet possessing a unique landscape shaped by the sea and light, Yokohama is one of Japan's most spectacular aerial destinations.",
  items: [
    {
      icon: "ri-building-line",
      title: "The Skyscrapers of Minato Mirai",
      desc: "Landmark Tower, Queen's Square, Cosmo Clock — Yokohama's symbols come into breathtaking close-up. A sense of scale that can never be experienced from the ground.",
    },
    {
      icon: "ri-route-line",
      title: "The Dynamic Bay Bridge",
      desc: "A bird's-eye view of the 860-meter suspension bridge in all its glory. At night, its illumination is like a glowing sculpture. The thrill of passing right over the bridge is unforgettable.",
    },
    {
      icon: "ri-store-2-line",
      title: "Red Brick Warehouse & Yamashita Park",
      desc: "Meiji-era red brick architecture alongside a lush seaside park — a uniquely Yokohama landscape where history and nature coexist, seen from above.",
    },
    {
      icon: "ri-ship-line",
      title: "The Harmony of Light Unique to a Port City",
      desc: "The lights of ships and the glow of the city reflect off the water — a collaboration of sea and light that is uniquely Yokohama. Beautiful from any time of day, from a world above ground.",
    },
  ],
};

export const viewData = {
  label: "VIEW",
  title: "What Will You See?",
  description:
    "Here are the iconic viewpoints you can enjoy on a Yokohama Helicopter Cruise. Every one of them is a breathtaking sight from a perspective only available from the sky.",
  spots: [
    { name: "Landmark Tower", desc: "One of Japan's iconic skyscrapers, seen up close. The sense of scale shifts as you gain altitude.", time: "Day & Night" },
    { name: "Cosmo Clock 21", desc: "One of the world's largest clock ferris wheels. At night, its illumination glitters like a distant jewel.", time: "Night view recommended" },
    { name: "Bay Bridge", desc: "The dramatic illuminated suspension bridge, seen close-up. The thrill of passing nearby is extraordinary.", time: "Night view recommended" },
    { name: "Red Brick Warehouse", desc: "A beautiful contrast of historic architecture and modern light, seen from the waterside.", time: "Night view recommended" },
    { name: "Yamashita Park", desc: "A lush park overlooking the harbor, with the Hikawa Maru ship. In the daytime, the blue sea and white hull are vivid.", time: "Daytime recommended" },
    { name: "Mt. Fuji (distant view)", desc: "On a clear day, you can even see the silhouette of Mt. Fuji from Yokohama's sky — a luxurious double spectacle.", time: "Day & Sunset" },
  ],
};

export const timeComparison = {
  label: "TIME",
  title: "Day / Sunset / Night — What's the Difference?",
  subtitle: "Yokohama reveals a completely different face depending on the time of day. Choose the time slot that best matches your purpose.",
  items: [
    {
      type: "day",
      title: "Daytime",
      desc: "The blue sky and sea, lush parks, and the vivid white of ships. On a clear day, you can see the silhouette of Mt. Fuji in the distance — ideal for photography. Best for sightseeing and family visits.",
      image:
        "https://readdy.ai/api/search-image?query=Aerial%20daytime%20view%20of%20Yokohama%20bay%20and%20Minato%20Mirai%20from%20helicopter%2C%20clear%20blue%20sky%2C%20bright%20natural%20lighting%2C%20white%20ships%20in%20harbor%2C%20green%20parks%2C%20modern%20skyscrapers%2C%20clean%20editorial%20aviation%20photography%2C%20no%20text&width=900&height=560&seq=day02&orientation=landscape",
      recommend: "Sightseeing · Family · Photography · First-time visitors",
      priceNote: "Relatively affordable pricing",
    },
    {
      type: "sunset",
      title: "Sunset (Magic Hour)",
      desc: "About 30 minutes around sunset. The sky shifts from orange to pink, then to deep navy — the most romantic time of day. The most popular choice for proposals and anniversaries.",
      image:
        "https://readdy.ai/api/search-image?query=Aerial%20view%20of%20Yokohama%20bay%20bridge%20and%20skyline%20at%20sunset%20golden%20hour%2C%20orange%20and%20deep%20navy%20gradient%20sky%2C%20warm%20ambient%20light%20reflecting%20on%20water%2C%20romantic%20helicopter%20tour%20experience%2C%20editorial%20photography%2C%20no%20text&width=900&height=560&seq=sunset02&orientation=landscape",
      recommend: "Date · Anniversary · Proposal · Surprise",
      priceNote: "Popular time slot — book early",
    },
    {
      type: "night",
      title: "Night View",
      desc: "The dazzling night skyline that defines Yokohama. The vista of Bay Bridge and Minato Mirai at night delivers emotion that is simply impossible to find on the ground. Overwhelmingly popular for dates, anniversaries, and gifts.",
      image:
        "https://readdy.ai/api/search-image?query=Aerial%20night%20view%20of%20Yokohama%20Minato%20Mirai%20skyline%20with%20thousands%20of%20sparkling%20city%20lights%2C%20illuminated%20Bay%20Bridge%2C%20deep%20navy%20sky%2C%20helicopter%20perspective%2C%20breathtaking%20nightscape%2C%20editorial%20luxury%20travel%20photography%2C%20no%20text&width=900&height=560&seq=night02&orientation=landscape",
      recommend: "Date · Anniversary · Proposal · Gift · Surprise",
      priceNote: "Most popular — booking 1–2 months ahead recommended",
    },
  ],
};

export const plansData = {
  label: "PLAN",
  title: "Recommended Yokohama Plans",
  subtitle: "Three courses to choose from based on your purpose and budget. All plans include dedicated staff support.",
  plans: [
    {
      name: "Night View Short",
      duration: "~15 min",
      route: "Minato Mirai Illumination → Bay Bridge Circuit",
      price: "Please inquire",
      priceNote: "From 2 guests",
      description: "A quick, accessible course to enjoy Yokohama's night view. Great for first-timers or those looking for a lighter introduction.",
      features: ["2–3 guests", "In-cabin drink service", "Safety equipment rental", "Day-of attendant"],
      popular: false,
      image:
        "https://readdy.ai/api/search-image?query=Helicopter%20cabin%20interior%20with%20soft%20warm%20lighting%20at%20night%2C%20leather%20seats%2C%20panoramic%20window%20showing%20city%20lights%20outside%2C%20luxury%20aviation%20experience%2C%20dark%20navy%20sky%2C%20intimate%20atmosphere%2C%20editorial%20photography%2C%20no%20text&width=640&height=400&seq=yplan04&orientation=landscape",
    },
    {
      name: "Night View Standard",
      duration: "~25 min",
      route: "Minato Mirai → Bay Bridge → Red Brick Warehouse → Yamashita Park",
      price: "Please inquire",
      priceNote: "From 2 guests",
      description: "The most popular course, covering Yokohama's iconic spots at a relaxed pace. Perfect for anniversary dates and surprises.",
      features: ["2–3 guests", "In-cabin drink service", "Commemorative photo option", "Day-of attendant", "Priority boarding slot"],
      popular: true,
      image:
        "https://readdy.ai/api/search-image?query=Helicopter%20flying%20over%20illuminated%20Yokohama%20bay%20bridge%20at%20night%2C%20deep%20navy%20sky%20with%20city%20lights%2C%20luxury%20aerial%20photography%2C%20editorial%20style%2C%20romantic%20atmosphere%2C%20no%20text&width=640&height=400&seq=yplan05&orientation=landscape",
    },
    {
      name: "Sunset + Night View Premium",
      duration: "~30 min",
      route: "Magic Hour around sunset → Transition to night view",
      price: "Please inquire",
      priceNote: "From 2 guests",
      description: "Experience from the sky the moment the sunset gives way to the night skyline — the most extraordinary and luxurious course.",
      features: ["2–3 guests", "In-cabin drink service", "Commemorative photo option", "Day-of attendant", "Mt. Fuji view (clear weather)", "Priority boarding slot"],
      popular: false,
      image:
        "https://readdy.ai/api/search-image?query=Helicopter%20aerial%20view%20of%20coastal%20Japanese%20city%20during%20dramatic%20sunset%20transition%2C%20warm%20orange%20sky%20merging%20with%20deep%20navy%2C%20bay%20water%20reflecting%20colors%2C%20luxury%20travel%20experience%2C%20editorial%20photography%2C%20no%20text&width=640&height=400&seq=yplan06&orientation=landscape",
    },
  ],
};

export const recommendByTime = {
  label: "RECOMMEND",
  title: "Which Plan Is Right for You?",
  subtitle: "We will suggest the best plan and time slot to match your purpose and situation.",
  items: [
    {
      tag: "First-Time",
      time: "15-min Night View Short",
      for: "First-time helicopter tour guests",
      scene: "Want to try it lightly · Looking for an accessible price",
      why: "Takes in the core views in a short time. Easy to get started without pressure.",
    },
    {
      tag: "Anniversary",
      time: "25-min Night View Standard",
      for: "Anniversary dates and couples",
      scene: "Birthday · Wedding anniversary · Special date night",
      why: "Enjoy the classic night view to the fullest. 25 minutes of relaxed flight to savor the scenery.",
    },
    {
      tag: "Proposal",
      time: "30-min Sunset + Night View Premium",
      for: "Those who want the most memorable setting",
      scene: "Proposal · Surprise · A once-in-a-lifetime experience",
      why: "The transition from sunset to night view is the most romantic. 30 minutes of rich, immersive experience.",
    },
  ],
};

export const scenesData = {
  label: "SCENE",
  title: "Popular Ways to Use This Experience",
  subtitle: "Anniversary, proposal, sightseeing, gift — for life's most precious moments, a view from the sky.",
  items: [
    {
      tag: "Anniversary",
      title: "Anniversary Date",
      desc: "For a wedding anniversary or birthday, step away from daily life for an extraordinary experience. Time spent in front of the night view with someone precious becomes a lifelong treasure. Surprise gift tickets are also very popular.",
      image:
        "https://readdy.ai/api/search-image?query=Couple%20holding%20hands%20inside%20luxury%20helicopter%20cabin%20at%20night%20with%20sparkling%20city%20view%20through%20window%2C%20warm%20golden%20interior%20lighting%2C%20romantic%20anniversary%20celebration%2C%20dark%20navy%20sky%2C%20editorial%20photography%2C%20no%20text&width=700&height=440&seq=yscene05&orientation=landscape",
    },
    {
      tag: "Proposal",
      title: "The Perfect Proposal Setting",
      desc: "Share what's in your heart at the ultimate location. A proposal in front of the night view is the most unforgettable moment you can create. Talk to our staff in advance and we can even help with timing.",
      image:
        "https://readdy.ai/api/search-image?query=Romantic%20proposal%20moment%20inside%20helicopter%20at%20night%20with%20stunning%20illuminated%20city%20skyline%20visible%20through%20panoramic%20window%2C%20soft%20warm%20lighting%2C%20luxury%20special%20occasion%2C%20dark%20navy%20sky%2C%20editorial%20photography%2C%20no%20text&width=700&height=440&seq=yscene06&orientation=landscape",
    },
    {
      tag: "Sightseeing",
      title: "A Special Sightseeing Experience in Yokohama",
      desc: "Rediscover the charm of Yokohama from a perspective you have never seen before. The perfect finale to any Yokohama visit. Suitable for families, friends — anyone can enjoy a special moment together.",
      image:
        "https://readdy.ai/api/search-image?query=Aerial%20daytime%20view%20of%20Yokohama%20port%20area%20from%20helicopter%20showing%20red%20brick%20warehouses%2C%20bay%20bridge%2C%20and%20Minato%20Mirai%20towers%2C%20clear%20blue%20sky%2C%20bright%20sightseeing%20experience%2C%20editorial%20travel%20photography%2C%20no%20text&width=700&height=440&seq=yscene07&orientation=landscape",
    },
    {
      tag: "Gift",
      title: "The Perfect Gift for Someone Special",
      desc: "An experience gift delivers emotion that no physical gift ever could. Surprise the people you love with an AIRDOA gift ticket — an aerial experience unlike anything else. Gift wrapping available.",
      image:
        "https://readdy.ai/api/search-image?query=Luxury%20gift%20box%20with%20gold%20ribbon%20and%20small%20card%20on%20dark%20navy%20marble%20surface%2C%20warm%20ambient%20lighting%2C%20elegant%20premium%20gift%20presentation%2C%20champagne%20gold%20tones%2C%20minimalist%20editorial%20photography%2C%20no%20text&width=700&height=440&seq=yscene08&orientation=landscape",
    },
  ],
};

export const flowData = {
  label: "FLOW",
  title: "From Booking to Boarding",
  subtitle: "First-timers are welcome. AIRDOA guides you every step of the way.",
  steps: [
    {
      num: "01",
      title: "Inquire & Book Online",
      desc: "Enter your preferred date, time, and number of guests. Our dedicated staff will suggest the best plan and confirm availability instantly.",
    },
    {
      num: "02",
      title: "Pre-Departure Guide Email",
      desc: "We send detailed guidance on the day-of schedule, attire, what to bring, and helipad access. Questions are welcome any time.",
    },
    {
      num: "03",
      title: "Arrive at the Helipad",
      desc: "Check in 30 minutes before boarding. After the safety briefing, meet your pilot. Commemorative photo here if desired (optional).",
    },
    {
      num: "04",
      title: "Take to the Sky",
      desc: "The engine starts and the aircraft lifts off. Breathtaking views that cannot be seen from the ground unfold before your eyes.",
    },
    {
      num: "05",
      title: "Land & Linger",
      desc: "After landing, commemorative photo data will be sent to you later. Head home carrying the warm glow of an extraordinary experience. Feel free to share on social media.",
    },
  ],
};

export const notesData = {
  label: "NOTES",
  title: "Important Notes for the Day",
  subtitle: "Please review these before your visit for a safe and comfortable experience. Questions are welcome any time.",
  items: [
    {
      icon: "ri-time-line",
      title: "Arrive 30 Minutes Early — Essential",
      desc: "Please arrive at the helipad at least 30 minutes before your scheduled boarding time. Late arrivals may not be permitted to board.",
    },
    {
      icon: "ri-t-shirt-line",
      title: "Attire Guide",
      desc: "Skirts and heels are fine. However, since the helipad is outdoors, we recommend stable footwear. Please dress appropriately for the season.",
    },
    {
      icon: "ri-camera-line",
      title: "Photos & Videos Welcome",
      desc: "Photos and videos with a smartphone or camera are permitted. Please be careful of vibration inside the cabin and avoid hitting the windows.",
    },
    {
      icon: "ri-cloud-windy-line",
      title: "Weather Cancellations",
      desc: "Safety is our top priority. If conditions are poor, we will cancel the flight. In that case, rescheduling or a full refund is available.",
    },
    {
      icon: "ri-car-line",
      title: "Parking & Transfer",
      desc: "Parking is available near the helipad (limited spaces). A transfer service is also available as an optional add-on. Please consult us at the time of booking.",
    },
    {
      icon: "ri-goblet-line",
      title: "Alcohol & Illness",
      desc: "Boarding after consuming alcohol may not be permitted for safety reasons. If you are unwell, please consider cancelling rather than pushing through.",
    },
  ],
};

export const accessData = {
  label: "ACCESS",
  title: "Getting to the Helipad",
  subtitle: "Departing from a helipad in Yokohama city. Accessible by train, car, or taxi.",
  address: "[Detailed address provided after reservation is confirmed]",
  methods: [
    {
      type: "train",
      icon: "ri-train-line",
      title: "By Train",
      desc: "Approximately 5–10 minutes by taxi from the nearest station. Walking is also possible. Detailed directions will be emailed after your reservation is confirmed.",
    },
    {
      type: "car",
      icon: "ri-car-line",
      title: "By Car",
      desc: "Parking is available near the helipad but is limited. Please let us know at the time of booking if you plan to drive.",
    },
    {
      type: "taxi",
      icon: "ri-taxi-line",
      title: "By Taxi or Transfer",
      desc: "Approximately 5–10 minutes by taxi from the nearest station. A pick-up service is also available as an optional add-on. Please consult us at the time of booking.",
    },
  ],
  tips: [
    "Please arrive at least 30 minutes before your scheduled boarding time",
    "The helipad is an outdoor facility. Please bring an umbrella on rainy days",
    "Parking is available by prior reservation",
    "If you are running late, please call us immediately",
    "The helipad is located in a residential area. Please be considerate of local residents",
  ],
};

export const faqPreview = {
  label: "FAQ",
  title: "Frequently Asked Questions",
  subtitle: "Answers to common questions before your visit. For anything else, please contact us.",
  items: [
    {
      q: "Is it okay if it's my first time?",
      a: "Absolutely. The majority of our guests are first-timers. We will send detailed guidance in advance, and our staff will take good care of you on the day. The pilot also takes pride in communicating warmly with guests.",
    },
    {
      q: "Where in Yokohama do we depart from?",
      a: "We depart from a helipad within Yokohama city. Detailed address and access information will be emailed after your reservation is confirmed. Accessible by train, car, or taxi.",
    },
    {
      q: "Can just 2 people book?",
      a: "Yes, bookings are available from 2 guests. Ideal for couples. The aircraft accommodates 2–3 guests, giving you an intimate and private space.",
    },
    {
      q: "How much does it cost?",
      a: "Prices vary by plan and time slot. We offer multiple courses from the Night View Short (~15 min) to the Sunset + Night View Premium (~30 min). Please check our Pricing & Plans page or contact us for details.",
    },
    {
      q: "What if the weather is bad?",
      a: "Safety is our top priority. Flights may be cancelled at the operator's discretion due to poor weather. We will offer rescheduling or a full refund. Weather decisions are made in coordination with the operator right up until just before boarding.",
    },
    {
      q: "Can I purchase this as a gift?",
      a: "Yes, AIRDOA gift tickets are available. You can give someone special a truly unique aerial experience. Gift wrapping is also available. Please see our Gift Ticket page for details.",
    },
  ],
};

export const reviewsData = {
  label: "VOICE",
  title: "Guest Testimonials",
  subtitle: "Reviews from guests who have enjoyed Yokohama Helicopter Cruising.",
  items: [
    {
      name: "K.Y.",
      age: "30s · Couple",
      scene: "Wedding Anniversary",
      comment: "The moment I saw Minato Mirai from the sky, I was speechless with emotion. The pilot was wonderful and we felt completely at ease. The 25-minute course flew by in an instant. It became a memory we will treasure for the rest of our lives.",
    },
    {
      name: "S.M.",
      age: "20s · Couple",
      scene: "Date",
      comment: "It was my first time in a helicopter, but the staff guided us so carefully that I had no anxiety at all. When the Bay Bridge came into view right in front of us, I got goosebumps. My girlfriend was overjoyed — it was the best date ever.",
    },
    {
      name: "A.T.",
      age: "40s · Couple",
      scene: "Birthday Surprise",
      comment: "I used this as a birthday surprise for my wife. We chose the sunset course, and the transition from the setting sun to the night skyline was absolutely stunning. The commemorative photo data was beautiful — we have it framed on our wall.",
    },
    {
      name: "R.K.",
      age: "30s · Male",
      scene: "Proposal",
      comment: "I used the Night View Standard for a proposal. I coordinated with the staff in advance and handed over the ring at the moment the Bay Bridge came into view. She said yes! I couldn't be happier.",
    },
  ],
};

export const safetyData = {
  label: "SAFETY",
  title: "Our Commitment to Safety",
  items: [
    {
      icon: "ri-shield-check-line",
      title: "MLIT-Certified Operators",
      desc: "All partnered operators are licensed businesses certified by the Ministry of Land, Infrastructure, Transport and Tourism. We rigorously review maintenance systems, pilot credentials, and safety records.",
    },
    {
      icon: "ri-tools-line",
      title: "Thorough Maintenance Management",
      desc: "Regular aircraft inspections and maintenance are carried out in accordance with established maintenance plans. We never compromise on safety.",
    },
    {
      icon: "ri-vip-crown-line",
      title: "Experienced Pilots",
      desc: "Pilots hold strict qualifications and have accumulated extensive flight hours. They take pride in communicating warmly with guests.",
    },
    {
      icon: "ri-cloud-line",
      title: "Strict Weather Monitoring",
      desc: "Safety first. We make calm, deliberate cancellation decisions in cases of poor visibility, strong winds, or thunderstorms, coordinating with operators right up until just before boarding.",
    },
  ],
};

export const midCTA = {
  title: "Check courses and pricing first",
  subtitle: "Find the course that best matches your purpose",
  ctas: [
    { label: "View Pricing & Plans", href: "/pricing", type: "primary" },
    { label: "Inquire About Booking", href: "/contact", type: "ghost" },
  ],
};

export const finalCTA = {
  title: "Make Yokohama's Sky\nYour Special Memory",
  subtitle: "Breathtaking views that cannot be seen from the ground are waiting for you. First-timers are in safe hands with AIRDOA.",
  ctas: [
    { label: "View Pricing & Plans", href: "/pricing", type: "primary" },
    { label: "Inquire About Booking", href: "/contact", type: "ghost" },
  ],
};
